import * as React from 'react';
import type { LucideIcon } from 'lucide-react';
import { iconColorMap } from '../../shared/styleTokens';

/**
 * Icon sizes based on the design system specification
 * - xs: 14px - Inline with small text
 * - sm: 16px - Buttons, form fields
 * - md: 20px - Standard icons
 * - lg: 24px - Emphasis, headers
 * - xl: 32px - Empty states, features
 */
export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * Icon color variants
 * - inherit: Uses currentColor (default)
 * - primary: Deep navy (text-primary-700)
 * - secondary: Neutral-600 (text-secondary-600)
 * - muted: Neutral-500 (text-secondary-500)
 * - error: Error red (text-error-700)
 * - success: Success green (text-success-700)
 * - warning: Warning amber (text-warning-700)
 */
export type IconColor = 'inherit' | 'primary' | 'secondary' | 'muted' | 'error' | 'success' | 'warning';

export interface IconProps {
  /**
   * The Lucide icon component to render
   */
  icon: LucideIcon;
  /**
   * Size of the icon
   * @default 'md'
   */
  size?: IconSize;
  /**
   * Color of the icon
   * @default 'inherit'
   */
  color?: IconColor;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Accessible label for the icon. Required for standalone icons.
   * If not provided, aria-hidden will be set to true automatically.
   */
  'aria-label'?: string;
  /**
   * Whether the icon is purely decorative.
   * Defaults to true if aria-label is not provided.
   */
  'aria-hidden'?: boolean;
}

/**
 * Size mapping to exact pixel values from the design spec
 */
const sizeMap: Record<IconSize, number> = {
  xs: 14,
  sm: 16,
  md: 20,
  lg: 24,
  xl: 32,
};

/**
 * Stroke width mapping - xl uses 2px, others use 1.5px
 */
const strokeWidthMap: Record<IconSize, number> = {
  xs: 1.5,
  sm: 1.5,
  md: 1.5,
  lg: 1.5,
  xl: 2,
};

/**
 * Color mapping to CSS values from the design spec
 */
const colorMap: Record<IconColor, string> = iconColorMap;

/**
 * Icon component wrapper for Lucide icons with consistent sizing and styling.
 *
 * Provides a standardized API for icon usage throughout the design system,
 * ensuring consistent sizes, colors, and accessibility attributes.
 *
 * @example
 * // Basic usage
 * import { Search } from 'lucide-react';
 * <Icon icon={Search} />
 *
 * @example
 * // With size and color
 * import { AlertCircle } from 'lucide-react';
 * <Icon icon={AlertCircle} size="lg" color="error" />
 *
 * @example
 * // Accessible standalone icon
 * import { Settings } from 'lucide-react';
 * <Icon icon={Settings} aria-label="Settings" />
 *
 * @example
 * // Decorative icon (default when no aria-label)
 * import { ChevronDown } from 'lucide-react';
 * <Icon icon={ChevronDown} size="sm" />
 */
const Icon = React.forwardRef<SVGSVGElement, IconProps>(
  (
    {
      icon: IconComponent,
      size = 'md',
      color = 'inherit',
      className = '',
      'aria-label': ariaLabel,
      'aria-hidden': ariaHidden,
      ...props
    },
    ref
  ) => {
    // Determine accessibility attributes
    // If aria-label is provided, icon is meaningful (not hidden)
    // If no aria-label, icon is decorative (hidden from screen readers)
    const isHidden = ariaHidden ?? !ariaLabel;

    const iconSize = sizeMap[size];
    const strokeWidth = strokeWidthMap[size];
    const iconColorValue = colorMap[color];

    // When color is 'inherit' (value is 'currentColor'), no text-color class is needed.
    // For all other values, iconColorValue is a Tailwind text-color class (e.g. 'text-primary-700')
    // and the SVG uses 'currentColor' to inherit from the class.
    const colorClass = iconColorValue === 'currentColor' ? '' : iconColorValue;
    const combinedClassName = [colorClass, className].filter(Boolean).join(' ');

    return (
      <IconComponent
        ref={ref}
        width={iconSize}
        height={iconSize}
        strokeWidth={strokeWidth}
        color="currentColor"
        className={combinedClassName}
        aria-label={ariaLabel}
        aria-hidden={isHidden}
        role={ariaLabel ? 'img' : undefined}
        {...props}
      />
    );
  }
);

Icon.displayName = 'Icon';

export { Icon };
